import { pickQty } from '../pricing/sources';
import {
    pickLocale,
    type Component,
    type Characteristic,
    type Value,
} from '../schema';
import { joinCombinedSku } from './combinedSku';
import type { SelectionsMap, UploadedFile } from '../state/selections';
import type { EvaluatedMap } from '../eval/types';
import { resolveCustomFields, type CustomFieldEntry } from './customFields';

export interface AssetEntry {
    value: string | string[] | number | null;
    label: string;
    valueLabel: string | string[];
    valueId?: number | number[] | null;
    valueQty?: number | number[];
    sku?: string;
    choiceSku?: string | string[];
    /** Per-Characteristic upload metadata for `fileupload` types. The
     *  `value` field shows the comma-joined filenames so simple
     *  `{{characteristic:NAME.value}}` placeholders read like any other
     *  field; the `files` array carries the full per-file metadata for
     *  templates that need the asset id or storage folder. */
    files?: ReadonlyArray<UploadedFile>;
    /** The Characteristic's merchant-defined fields, keyed by field number. */
    customFields?: Record<string, CustomFieldEntry>;
    /** The picked Value's merchant-defined fields (single picks only, like legacy). */
    choiceCustomFields?: Record<string, CustomFieldEntry>;
    visibility: number;
}

export type AssetPayload = Record<string, AssetEntry | string | undefined> & {
    _component_title?: string;
    /** SKU of the configured product (`A-B-C`), see {@link joinCombinedSku}. */
    _combined_sku?: string;
    _locale?: string;
    _combined_image?: string;
    /**
     * Base64 ALTCHA proof. Populated when the Component contains a `captcha`
     * Characteristic and the user solved its widget. The Save-Button-Action
     * backend verifies this against {@link \App\Services\Altcha\AltchaService}
     * before processing email/pdf submissions — see
     * v3/docs/OPEN_ITEMS.md sektion 8.
     */
    altcha?: string;
};

const VISIBILITY_CODE: Record<Characteristic['visibility'], number> = {
    hidden:  0,
    visible: 1,
    shop:    2,
    backend: 3,
};

/**
 * Build the alt_title-keyed asset JSON the legacy storefront produces in
 * `configurator.js:generateHeaderValue`. Two consumers today:
 *
 *  - {@link ActionButtonView} POSTs this to the savebutton backend
 *    (PdfRenderer, EmailSender, PlaceholderRenderer all read by alt_title).
 *  - The Shopify-plugin loader base64-encodes this into the saveSnapshot
 *    endpoint payload; the cart-transform function and order-render later
 *    read the same shape back.
 *
 * Legacy excludes `shop`-only visibility (CONFIGENTO.OPTION_VISIBLE_SHOP,
 * configurator.js:858). We mirror that. Calculated types (expression /
 * combi / matrixvalue / productattribute / webservice) are emitted with
 * the evaluator output as their `value` so the recipient template can
 * `{{characteristic:NAME.value}}` them just like any other field.
 *
 * A Characteristic a rule has removed keeps its key but is emitted blank
 * with `visibility: 0` — legacy parity (configurator.js:894-912 forces
 * `visibility = 0`, and `calculatePrice()` returns 0 while blacklisted).
 * Dropping the key instead would break every downstream consumer that
 * looks entries up by `alt_title`: the PDF/email templates, the
 * Placeholder renderer, and the Shopify cart-transform.
 */
export function buildAssetPayload(args: {
    component: Component;
    selections: SelectionsMap;
    evaluated: EvaluatedMap;
    locale: string | undefined;
    uiLocale: string;
    /** Characteristic ids a rule has removed. Pass the rule result's
     *  `hiddenCharacteristics`; omit only when no rule pass ran. */
    hiddenCharacteristics?: ReadonlySet<number> | undefined;
}): AssetPayload {
    const { component, selections, evaluated, locale, uiLocale } = args;
    const hidden = args.hiddenCharacteristics ?? EMPTY_HIDDEN;
    const defs = component.customFieldDefinitions ?? NO_CUSTOM_FIELDS;
    const out: AssetPayload = {};
    const skuEntries: AssetEntry[] = [];

    let captchaPayload: string | undefined;

    for (const group of component.groups) {
        for (const c of group.characteristics) {
            if (c.type === 'savebutton') continue;
            if (c.visibility === 'shop') continue;
            if (c.type === 'captcha') {
                // Captcha doesn't get a per-Characteristic entry — its proof
                // flows as a top-level field below so the server-side
                // verifier can pick it up without parsing the asset entries.
                const sel = selections[c.id];
                if (sel?.kind === 'captcha' && sel.payload !== '') {
                    captchaPayload = sel.payload;
                }
                continue;
            }
            if (!c.altTitle) continue;
            const entry = buildEntry(c, selections, evaluated, locale);
            if (!entry) continue;
            // Legacy writes the Characteristic's own SKU on every entry; the
            // Magento plugin falls back to it when no Value SKU is chosen.
            const charSku = c.sku?.trim();
            if (charSku) entry.sku = charSku;
            // Legacy writes both field sets into the header; the Magento
            // plugin stores them on the order item (custom_fields /
            // choice_custom_fields) and the mail placeholders print them.
            const charFields = resolveCustomFields(c.customFields ?? {}, defs.characteristics);
            if (Object.keys(charFields).length > 0) entry.customFields = charFields;
            const picked = pickedSingleValue(c, selections);
            if (picked) {
                const valueFields = resolveCustomFields(picked.customFields ?? {}, defs.values);
                if (Object.keys(valueFields).length > 0) entry.choiceCustomFields = valueFields;
            }
            out[c.altTitle] = hidden.has(c.id) ? blankEntry(entry) : entry;
            if (!hidden.has(c.id)) skuEntries.push(entry);
        }
    }

    // Built here once so every consumer (shop plugins, mails, the summary)
    // reads the same value instead of re-deriving it.
    const combinedSku = joinCombinedSku(skuEntries);
    if (combinedSku !== '') out._combined_sku = combinedSku;

    const title = pickLocale(component.title, locale);
    if (title) out._component_title = title;
    out._locale = uiLocale;
    if (captchaPayload !== undefined) out.altcha = captchaPayload;
    return out;
}

function buildEntry(
    c: Characteristic,
    selections: SelectionsMap,
    evaluated: EvaluatedMap,
    locale: string | undefined,
): AssetEntry | null {
    const label = pickLocale(c.title, locale) ?? c.altTitle ?? '';
    const visibility = VISIBILITY_CODE[c.visibility];
    const sel = selections[c.id];

    switch (c.type) {
        case 'text':
        case 'area': {
            const value = sel?.kind === 'text' ? sel.value : '';
            return { value, label, valueLabel: value, visibility };
        }
        case 'select':
        case 'radiobuttons':
        case 'package': {
            const id = sel?.kind === 'single' ? sel.valueId : null;
            const v = id != null ? findValue(c, id) : undefined;
            const qtyMode = 'quantityMode' in c ? c.quantityMode : undefined;
            return {
                value: v?.value ?? '',
                label,
                valueLabel: pickLocale(v?.title, locale) ?? v?.value ?? '',
                valueId: id,
                choiceSku: v?.sku,
                // Admin "Menge": legacy writes the pick's quantity as `valueQty`.
                ...(qtyMode && v ? { valueQty: pickQty({ quantityMode: qtyMode }, sel?.kind === 'single' ? sel.qty : undefined) } : {}),
                visibility,
            };
        }
        case 'multiselect':
        case 'checkbox':
        case 'listimage': {
            const ids =
                sel?.kind === 'multi'
                    ? sel.valueIds
                    : sel?.kind === 'single' && sel.valueId !== null
                      ? [sel.valueId]
                      : [];
            const vs = ids.map((id) => findValue(c, id)).filter((v): v is Value => !!v);
            const qtyMode = 'quantityMode' in c ? c.quantityMode : undefined;
            const mode = { quantityMode: qtyMode };
            const qtyOf = (valueId: number) =>
                sel?.kind === 'multi' ? pickQty(mode, sel.qtys?.[valueId]) : pickQty(mode, sel?.kind === 'single' ? sel.qty : undefined);
            return {
                value: vs.map((v) => v.value ?? ''),
                label,
                valueLabel: vs.map((v) => pickLocale(v.title, locale) ?? v.value ?? ''),
                valueId: vs.map((v) => v.id),
                choiceSku: vs.map((v) => v.sku ?? ''),
                ...(qtyMode && vs.length > 0 ? { valueQty: vs.map((v) => qtyOf(v.id)) } : {}),
                visibility,
            };
        }
        case 'multiquantity': {
            if (sel?.kind !== 'quantities') {
                return { value: '', label, valueLabel: '', visibility };
            }
            // Preserve qty alongside valueId so the edit-restore round-trip
            // can reconstruct the quantities Record exactly. Two parallel
            // arrays — same shape legacy produced.
            const entries = Object.entries(sel.quantities).filter(([, qty]) => qty > 0);
            const ids: number[] = [];
            const qtys: number[] = [];
            for (const [idStr, qty] of entries) {
                ids.push(Number(idStr));
                qtys.push(qty);
            }
            const vs = ids.map((id) => findValue(c, id)).filter((v): v is Value => !!v);
            return {
                value: vs.map((v) => v.value ?? ''),
                label,
                valueLabel: vs.map((v) => pickLocale(v.title, locale) ?? v.value ?? ''),
                valueId: vs.map((v) => v.id),
                // Legacy writes the per-value SKUs here too; without them a
                // shop's part list only ever saw the Characteristic's own SKU.
                choiceSku: vs.map((v) => v.sku ?? ''),
                valueQty: qtys,
                visibility,
            };
        }
        case 'expression':
        case 'combi':
        case 'matrixvalue':
        case 'productattribute':
        case 'webservice': {
            const result = evaluated.get(c.id);
            if (result === null || result === undefined) {
                return { value: '', label, valueLabel: '', visibility };
            }
            const str = typeof result === 'number' ? String(result) : result;
            return { value: str, label, valueLabel: str, visibility };
        }
        case 'fileupload': {
            // No upload yet → still emit a placeholder entry so templates
            // can show "(no files)" instead of `{{characteristic:foo.value}}`
            // surviving raw. Empty files array distinguishes "characteristic
            // exists, just unused" from "characteristic missing".
            if (sel?.kind !== 'files' || sel.files.length === 0) {
                return { value: '', label, valueLabel: '', visibility, files: [] };
            }
            const names = sel.files.map((f) => f.file);
            return {
                value: names.join(', '),
                label,
                valueLabel: names,
                files: sel.files,
                visibility,
            };
        }
        // Render-only / no-data types — skip from the payload entirely.
        case 'static':
        case 'chart':
        case 'savebutton':
        case 'captcha':
        case 'unknown':
            return null;
    }
}

const EMPTY_HIDDEN: ReadonlySet<number> = new Set();
const NO_CUSTOM_FIELDS = { characteristics: [], values: [] } as const;

/** The Value of a single-pick Characteristic, where legacy had an active choice. */
function pickedSingleValue(c: Characteristic, selections: SelectionsMap): Value | undefined {
    if (c.type !== 'select' && c.type !== 'radiobuttons' && c.type !== 'package') return undefined;
    const sel = selections[c.id];
    return sel?.kind === 'single' && sel.valueId !== null ? findValue(c, sel.valueId) : undefined;
}

/**
 * Strip an entry down to "this Characteristic exists but carries nothing",
 * mirroring what legacy produced for a blacklisted option: `visibility: 0`,
 * no active choice (so `valueLabel` / `valueId` / `valueQty` fall away) and
 * a zero price contribution.
 *
 * The Characteristic-level `sku` survives — legacy writes `part.sku` outside
 * its blacklist branch, and shops key inventory off it.
 */
function blankEntry(entry: AssetEntry): AssetEntry {
    const blanked: AssetEntry = {
        value: '',
        label: entry.label,
        valueLabel: '',
        visibility: 0,
    };
    if (entry.sku !== undefined) blanked.sku = entry.sku;
    // Characteristic-level, like `sku`; the Value's fields go with the pick.
    if (entry.customFields !== undefined) blanked.customFields = entry.customFields;
    // `fileupload` templates branch on the array's presence, not its length.
    if (entry.files !== undefined) blanked.files = [];
    return blanked;
}

function findValue(c: Characteristic, id: number): Value | undefined {
    if (!('values' in c)) return undefined;
    return c.values.find((v) => v.id === id);
}
